import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MAX_SCENE_TEXT_CHARS, type SceneIntExt } from '@scoutaround/script-breakdown-shared';

import type { SceneValidationRow } from '../../lib/api.js';
import { Icon, type IconName } from '../Icon.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { StatusPill } from '../ui/StatusPill.js';
import {
  INT_EXT_LABEL,
  STATUS_ICON,
  STATUS_LABEL,
  STATUS_STATE,
  linesWithOffsets,
  sceneLabel,
  settingIcon,
  warningRemedies,
  warningText,
  type WarningRemedy,
} from './sceneLabels.js';

export interface SceneEdit {
  slugline?: string;
  sceneNumber?: string | null;
  intExt?: SceneIntExt | null;
  timeOfDay?: string | null;
  text?: string | null;
}

type Mode = 'VIEW' | 'SPLIT' | 'MERGE' | 'EXCLUDE';

/** What a scene is, beside its heading: interior or exterior, the time, where its text came from. */
export function sceneFacts(scene: SceneValidationRow): string {
  return [
    scene.intExt === null ? 'Int./Ext. not stated' : (INT_EXT_LABEL[scene.intExt] ?? scene.intExt),
    scene.timeOfDay === null ? null : scene.timeOfDay.charAt(0) + scene.timeOfDay.slice(1).toLowerCase(),
    scene.textSource === null ? 'No text' : scene.textSource === 'TYPED' ? 'Typed text' : 'Screenplay text',
  ]
    .filter((part) => part !== null)
    .join(' · ');
}

/**
 * The marks a scene can carry besides its status — each a fact somebody may
 * need before confirming it, with the id the tests and the smoke walk read.
 */
export function SceneMarkers({ scene, idPrefix = 'scene' }: { scene: SceneValidationRow; idPrefix?: 'scene' | 'scene-panel' }) {
  const marks: ReactNode[] = [];
  if (scene.originalSlugline !== null && scene.originalSlugline !== scene.slugline) {
    marks.push(
      <span key="edited" className="sw-mark" title={`As written: ${scene.originalSlugline}`} data-testid={idPrefix === 'scene' ? `scene-edited-${scene.id}` : 'scene-panel-edited'}>
        Heading edited
      </span>,
    );
  }
  if (scene.origin === 'MANUAL') {
    marks.push(
      <span key="manual" className="sw-mark" data-testid={idPrefix === 'scene' ? `scene-manual-${scene.id}` : 'scene-panel-manual'}>
        {scene.textSource === 'SCREENPLAY' ? 'Split off' : 'Added by you'}
      </span>,
    );
  }
  if (scene.analysis === 'OUT_OF_DATE') {
    marks.push(
      <span key="ood" className="sw-mark" data-tone="outdated" data-testid={idPrefix === 'scene' ? `scene-out-of-date-${scene.id}` : 'scene-panel-out-of-date'}>
        Changed since the AI read it
      </span>,
    );
  } else if (scene.needsReading) {
    marks.push(
      <span key="unread" className="sw-mark" data-testid={idPrefix === 'scene' ? `scene-unread-${scene.id}` : 'scene-panel-unread'}>
        Not read by the AI yet
      </span>,
    );
  }

  return marks.length === 0 ? null : <>{marks}</>;
}

/** How each correction reads wherever it is offered: in the ⋯ menu and on a warning. */
const REMEDY: Record<WarningRemedy, { label: string; icon: IconName }> = {
  EDIT: { label: 'Edit heading', icon: 'pencil' },
  SPLIT: { label: 'Split scene…', icon: 'scissors' },
  MERGE: { label: 'Merge with next…', icon: 'link' },
  EXCLUDE: { label: 'Remove scene…', icon: 'trash' },
};

/**
 * ONE SCENE, IN THE DETAIL PANE — the review step 2 is for.
 *
 * Read top to bottom (the scene review card, 2026-11-05):
 * - a bar: which scene, how it stands, and the way to the one before and after;
 * - the heading, with where the scene is set as a tile beside it, the facts under
 *   it, and its conversations; "Edit heading" and the ⋯ menu at its right;
 * - what looks wrong, each warning carrying the correction it names;
 * - the screenplay's own words, then what a location for it must be like
 *   (read-only, from the location breakdown);
 * - a footer with the one thing to do: Confirm.
 *
 * The heading fields open only to be corrected, right under the heading they
 * correct. The structural corrections (split, merge, move, add after, remove)
 * live in the ⋯ menu; the warning that calls for one offers it in place too, so
 * "needs attention" is never a hunt. Merge and remove are asked about under the
 * header before they are done. A correction is saved when it is made; there is
 * nothing to "save" at the end.
 *
 * THE SCREENPLAY IS NOT EDITED HERE. A parsed scene's text is the uploaded
 * file's and is shown read-only; correcting a heading keeps the parsed one beside
 * it ("As written"). Only a scene a person added has text a person can type —
 * the owner request of 2026-10-29 — and it is labelled as typed.
 *
 * A CONFIRMED SCENE IS CLOSED TO CORRECTIONS (owner request, 2026-11-05). It
 * reads like a finished page: no heading fields, no corrections, typed text shown
 * read-only. Its footer and its ⋯ menu offer "Mark as not reviewed", which is what
 * opens it for corrections again, and the footer the next scene. Adding a scene
 * after it stays allowed, as the server allows it. The server refuses a
 * correction to it as well (`SCENE_CONFIRMED`), so this is the hint, not the gate.
 */
export function ScenePanel({
  scene,
  position,
  nextScene,
  mergedInto,
  hasPrevious,
  hasNext,
  canMoveUp,
  canMoveDown,
  nextFlagged,
  busy,
  error,
  refusal,
  focusOnMount = false,
  discuss,
  locations,
  onPrevious,
  onNext,
  onNextFlagged,
  onConfirm,
  onSave,
  onSplit,
  onMerge,
  onExclude,
  onRestore,
  onMove,
  onAddAfter,
}: {
  scene: SceneValidationRow;
  /** Where it sits in the list on screen: "12 of 93". */
  position: { index: number; total: number } | null;
  /** The scene after this one in the screenplay — what a merge would absorb. */
  nextScene: SceneValidationRow | null;
  /** For a merged scene: the one it went into. */
  mergedInto: SceneValidationRow | null;
  hasPrevious: boolean;
  hasNext: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** The next scene that needs attention, when there is another one. */
  nextFlagged: SceneValidationRow | null;
  busy: boolean;
  error: string | null;
  /** Why this person cannot correct the reading, or null when they can. */
  refusal: string | null;
  /** Take the keyboard as it opens — after a confirm moved here, not after a click in the list. */
  focusOnMount?: boolean;
  /** The scene's conversations control, when there is one. */
  discuss?: ReactNode;
  /** What a location for this scene must be like (`SceneLocationRequirements`), under the excerpt. */
  locations?: ReactNode;
  onPrevious: () => void;
  onNext: () => void;
  onNextFlagged: () => void;
  onConfirm: (confirmed: boolean) => void;
  onSave: (edit: SceneEdit) => void;
  onSplit: (input: { at: number; slugline: string }) => void;
  onMerge: () => void;
  onExclude: () => void;
  onRestore: () => void;
  onMove: (direction: 'UP' | 'DOWN') => void;
  onAddAfter: () => void;
}) {
  const [heading, setHeading] = useState(scene.slugline ?? '');
  const [number, setNumber] = useState(scene.sceneNumber ?? '');
  const [intExt, setIntExt] = useState(scene.intExt ?? '');
  const [timeOfDay, setTimeOfDay] = useState(scene.timeOfDay ?? '');
  const [text, setText] = useState(scene.text ?? '');
  const [editingAsked, setEditing] = useState(false);
  const [modeAsked, setMode] = useState<Mode>('VIEW');
  const [splitAt, setSplitAt] = useState<number | null>(null);
  const [splitHeading, setSplitHeading] = useState('');
  const region = useRef<HTMLElement | null>(null);
  const headingInput = useRef<HTMLInputElement | null>(null);
  const ask = useRef<HTMLDivElement | null>(null);

  const excluded = scene.status === 'EXCLUDED';
  const confirmed = scene.status === 'CONFIRMED';
  /** Open to corrections: neither left out of the list nor confirmed. */
  const correctable = !excluded && !confirmed;
  // A correction under way never outlives the scene being closed to them.
  const editing = editingAsked && correctable;
  const mode: Mode = correctable ? modeAsked : 'VIEW';
  const typed = scene.origin === 'MANUAL' && scene.textSource !== 'SCREENPLAY';
  const nextConfirmed = nextScene?.status === 'CONFIRMED';
  /** The heading was corrected: the screenplay's own words stay on show beside it. */
  const headingEdited = scene.originalSlugline !== null && scene.originalSlugline !== scene.slugline;
  const locked = refusal !== null || busy;

  useEffect(() => {
    if (focusOnMount) region.current?.focus();
  }, [focusOnMount]);

  // A correction chosen from the ⋯ menu or a warning takes the keyboard with it:
  // the menu item it was chosen from is gone, and the question is what to read next.
  useEffect(() => {
    if (editing) headingInput.current?.focus();
  }, [editing]);
  useEffect(() => {
    if (mode === 'SPLIT') {
      // The lines are the question: the suggested one when there is one, else the first.
      region.current?.querySelector<HTMLElement>('.scene-split-line[data-on="1"], .scene-split-line')?.focus();
    } else if (mode !== 'VIEW') {
      ask.current?.focus();
    }
  }, [mode]);

  const edit: SceneEdit = {};
  // Headings are stored upper case, as the screenplay writes them.
  if (heading.trim().toUpperCase() !== (scene.slugline ?? '')) edit.slugline = heading.trim();
  if (number.trim() !== (scene.sceneNumber ?? '')) edit.sceneNumber = number.trim() === '' ? null : number.trim();
  if (intExt !== (scene.intExt ?? '')) edit.intExt = intExt === '' ? null : (intExt as SceneIntExt);
  if (timeOfDay.trim().toUpperCase() !== (scene.timeOfDay ?? '')) {
    edit.timeOfDay = timeOfDay.trim() === '' ? null : timeOfDay.trim();
  }
  if (typed && text.replace(/\s+$/, '') !== (scene.text ?? '')) edit.text = text;

  const dirty = correctable && Object.keys(edit).length > 0;
  const headingEmpty = heading.trim() === '';
  const textTooLong = text.length > MAX_SCENE_TEXT_CHARS;
  const suggested = new Set(scene.suggestedSplitOffsets);
  const lines = scene.text === null ? [] : linesWithOffsets(scene.text);

  const cancelEdit = () => {
    setHeading(scene.slugline ?? '');
    setNumber(scene.sceneNumber ?? '');
    setIntExt(scene.intExt ?? '');
    setTimeOfDay(scene.timeOfDay ?? '');
    setText(scene.text ?? '');
    setEditing(false);
  };

  const startSplit = () => {
    setMode('SPLIT');
    const first = scene.suggestedSplitOffsets[0];
    if (first !== undefined && scene.text !== null) {
      setSplitAt(first);
      setSplitHeading(lines.find((l) => l.offset === first)?.line.trim() ?? '');
    }
  };

  /**
   * Each correction once: whether it can be made now, why not, and what it does —
   * read by the ⋯ menu and by the warnings alike, so the two never disagree.
   */
  const correction: Record<WarningRemedy, { disabled: boolean; why: string | undefined; run: () => void }> = {
    EDIT: {
      disabled: locked || editing,
      why: refusal ?? undefined,
      run: () => {
        setEditing(true);
      },
    },
    SPLIT: {
      disabled: locked || scene.text === null || mode === 'SPLIT',
      why: refusal ?? (scene.text === null ? 'This scene has no text to split' : undefined),
      run: startSplit,
    },
    MERGE: {
      disabled: locked || nextScene === null || nextConfirmed,
      why:
        refusal ??
        (nextScene === null
          ? 'This is the last scene'
          : nextConfirmed
            ? `Scene ${sceneLabel(nextScene)} is confirmed. Mark it as not reviewed to merge it.`
            : `Joins scene ${sceneLabel(nextScene)} to this one`),
      run: () => {
        setMode('MERGE');
      },
    },
    EXCLUDE: {
      disabled: locked,
      why: refusal ?? 'Not a scene: it leaves the list, nothing is deleted',
      run: () => {
        setMode('EXCLUDE');
      },
    },
  };

  const correctionItem = (id: string, kind: Exclude<WarningRemedy, 'EDIT'>, testId: string, extra: Partial<MenuItem> = {}): MenuItem => ({
    id,
    label: REMEDY[kind].label,
    icon: REMEDY[kind].icon,
    disabled: correction[kind].disabled,
    hint: correction[kind].why,
    testId,
    onSelect: correction[kind].run,
    ...extra,
  });

  const addAfterItem: MenuItem = {
    id: 'add',
    label: 'Add a scene after this one',
    icon: 'plus',
    disabled: locked,
    hint: refusal ?? undefined,
    testId: 'scene-panel-add-after',
    onSelect: onAddAfter,
  };

  // The save row follows what is being changed: under the heading fields, or
  // under the typed text when only the text was changed.
  const saveRow =
    editing || dirty ? (
      <div className="sw-save" data-testid="scene-panel-save-row">
        <button
          type="button"
          className="btn btn-ghost sw-save-btn"
          data-testid="scene-panel-save"
          disabled={!dirty || headingEmpty || textTooLong || locked}
          title={refusal ?? undefined}
          onClick={() => {
            onSave(edit);
          }}
        >
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" className="ctl" data-testid="scene-panel-edit-cancel" disabled={busy} onClick={cancelEdit}>
          Cancel
        </button>
        {dirty ? (
          <span className="sw-hint sw-unsaved" data-testid="scene-panel-unsaved">
            Unsaved changes
          </span>
        ) : null}
      </div>
    ) : null;

  const textReadOnly = scene.text !== null && mode !== 'SPLIT' && !(typed && correctable);

  return (
    <article
      ref={region}
      className="sw-detail"
      aria-label={`Scene ${sceneLabel(scene)} — review`}
      tabIndex={-1}
      data-testid={`scene-panel-${scene.id}`}
      data-status={scene.status}
    >
      {/* ---- which scene, how it stands, and the way to the next ---------- */}
      <div className="sw-detail-bar">
        <p className="sw-detail-kicker">
          <span>{`Scene ${sceneLabel(scene)}`}</span>
          {position === null ? null : (
            <span className="sw-detail-pos" data-testid="scene-panel-position" title="Its place in the list on the left">
              {`${String(position.index + 1)} of ${String(position.total)}`}
            </span>
          )}
        </p>
        <StatusPill state={STATUS_STATE[scene.status]} label={STATUS_LABEL[scene.status]} icon={STATUS_ICON[scene.status]} testId="scene-panel-status" />
        <span className="sw-detail-nav" role="group" aria-label="Move between scenes">
          <button type="button" className="ctl sw-nav-btn" data-testid="scene-panel-previous" aria-label="Previous scene" disabled={!hasPrevious} onClick={onPrevious}>
            <Icon name="chevronLeft" size={16} strokeWidth={2} />
            <span className="sw-nav-text">Previous</span>
          </button>
          <button type="button" className="ctl sw-nav-btn" data-dir="next" data-testid="scene-panel-next" aria-label="Next scene" disabled={!hasNext} onClick={onNext}>
            <span className="sw-nav-text">Next</span>
            <Icon name="chevronRight" size={16} strokeWidth={2} />
          </button>
        </span>
      </div>

      {nextFlagged === null ? null : (
        <button type="button" className="sw-next-flagged" data-testid="scene-panel-next-attention" onClick={onNextFlagged}>
          <Icon name="alert" size={14} />
          <span>{`Next scene needing attention: ${sceneLabel(nextFlagged)} — ${nextFlagged.slugline ?? 'untitled'}`}</span>
          <Icon name="arrowRight" size={14} />
        </button>
      )}

      {/* ---- the heading, and what can be done to the scene --------------- */}
      <header className="sw-detail-head">
        <span className="sw-setting" data-setting={scene.intExt ?? 'NONE'} aria-hidden="true">
          <Icon name={settingIcon(scene.intExt)} size={26} strokeWidth={1.6} />
        </span>
        <div className="sw-detail-id">
          <h3 className="sw-detail-heading" data-testid="scene-panel-title">
            {scene.slugline ?? 'Untitled scene'}
          </h3>
          <p className="sw-detail-facts">
            <span>{sceneFacts(scene)}</span>
            <SceneMarkers scene={scene} idPrefix="scene-panel" />
          </p>
          {headingEdited ? (
            <p className="sw-hint sw-as-written" data-testid="scene-panel-original-heading">
              As written in the screenplay: <span className="scene-mono">{scene.originalSlugline}</span>
            </p>
          ) : null}
          {discuss === undefined ? null : <div className="sw-detail-meta">{discuss}</div>}
        </div>
        <div className="sw-detail-tools">
          {correctable ? (
            <button
              type="button"
              className="ctl sw-tool"
              data-testid="scene-panel-edit"
              disabled={correction.EDIT.disabled}
              title={correction.EDIT.why}
              onClick={correction.EDIT.run}
            >
              <Icon name="pencil" size={15} /> Edit heading
            </button>
          ) : null}
          {correctable ? (
            <MoreMenu
              testId="scene-panel-more"
              label="Scene actions"
              buttonClassName="ctl sw-tool sw-tool-icon"
              items={[
                correctionItem('split', 'SPLIT', 'scene-panel-split'),
                correctionItem('merge', 'MERGE', 'scene-panel-merge'),
                {
                  id: 'up',
                  label: 'Move up one place',
                  icon: 'chevronUp',
                  disabled: locked || !canMoveUp,
                  hint: refusal ?? (canMoveUp ? undefined : 'Already the first scene'),
                  testId: 'scene-panel-move-up',
                  separatorBefore: true,
                  onSelect: () => {
                    onMove('UP');
                  },
                },
                {
                  id: 'down',
                  label: 'Move down one place',
                  icon: 'chevronDown',
                  disabled: locked || !canMoveDown,
                  hint: refusal ?? (canMoveDown ? undefined : 'Already the last scene'),
                  testId: 'scene-panel-move-down',
                  onSelect: () => {
                    onMove('DOWN');
                  },
                },
                addAfterItem,
                correctionItem('remove', 'EXCLUDE', 'scene-panel-exclude', { tone: 'danger', separatorBefore: true }),
              ]}
            />
          ) : confirmed ? (
            <MoreMenu
              testId="scene-panel-status-menu"
              label="Scene actions"
              buttonClassName="ctl sw-tool sw-tool-icon"
              items={[
                {
                  id: 'unconfirm',
                  label: 'Mark as not reviewed',
                  icon: 'undo',
                  disabled: locked,
                  hint: refusal ?? 'Opens the scene for corrections again',
                  testId: 'scene-panel-unconfirm',
                  onSelect: () => {
                    onConfirm(false);
                  },
                },
                { ...addAfterItem, separatorBefore: true },
              ]}
            />
          ) : null}
        </div>
      </header>

      {/* ---- the heading fields, right under the heading they correct ----- */}
      {editing ? (
        <section className="sw-edit" aria-label="Correct the scene heading" data-testid="scene-panel-edit-panel">
          <div className="sw-fields" data-testid="scene-panel-fields">
            <label className="sw-field sw-field-wide">
              <span className="sw-field-label">Scene heading</span>
              <input
                ref={headingInput}
                className="intake-input scene-mono"
                data-testid="scene-panel-heading"
                value={heading}
                disabled={locked}
                onChange={(e) => {
                  setHeading(e.target.value);
                }}
              />
            </label>
            <label className="sw-field">
              <span className="sw-field-label">Scene number</span>
              <input
                className="intake-input"
                data-testid="scene-panel-number"
                value={number}
                disabled={locked}
                placeholder="—"
                onChange={(e) => {
                  setNumber(e.target.value);
                }}
              />
            </label>
            <label className="sw-field">
              <span className="sw-field-label">Int./Ext.</span>
              <select
                className="intake-input"
                data-testid="scene-panel-int-ext"
                value={intExt}
                disabled={locked}
                onChange={(e) => {
                  setIntExt(e.target.value);
                }}
              >
                <option value="">Not stated</option>
                {(['INT', 'EXT', 'INT_EXT'] as const).map((v) => (
                  <option key={v} value={v}>
                    {INT_EXT_LABEL[v]}
                  </option>
                ))}
              </select>
            </label>
            <label className="sw-field">
              <span className="sw-field-label">Time of day</span>
              <input
                className="intake-input"
                data-testid="scene-panel-time"
                value={timeOfDay}
                disabled={locked}
                placeholder="—"
                onChange={(e) => {
                  setTimeOfDay(e.target.value);
                }}
              />
            </label>
          </div>
          {saveRow}
        </section>
      ) : null}

      {/* ---- a merge or a removal, asked before it is done ----------------- */}
      {mode === 'MERGE' && nextScene !== null ? (
        <div ref={ask} tabIndex={-1} className="sw-ask sw-detail-ask" data-testid="scene-merge-confirm" role="alertdialog" aria-labelledby="scene-merge-title">
          <p id="scene-merge-title" className="sw-ask-title">
            {`Merge scene ${sceneLabel(nextScene)} — ${nextScene.slugline ?? 'untitled'} — into this one?`}
          </p>
          <p className="mm">
            Its text joins this scene, and the locations and elements on it move here. It leaves the scene list and is
            kept under Excluded as merged. To undo it, split this scene again.
          </p>
          <div className="sw-ask-actions">
            <button type="button" className="ctl" data-testid="scene-merge-cancel" onClick={() => setMode('VIEW')}>
              Keep them apart
            </button>
            <button type="button" className="btn" data-testid="scene-merge-submit" disabled={locked} onClick={onMerge}>
              <Icon name="link" size={15} />
              Merge scenes
            </button>
          </div>
        </div>
      ) : mode === 'EXCLUDE' ? (
        <div
          ref={ask}
          tabIndex={-1}
          className="sw-ask sw-detail-ask"
          data-tone="danger"
          data-testid="remove-scene-confirm"
          role="alertdialog"
          aria-labelledby="remove-scene-title"
        >
          <p id="remove-scene-title" className="sw-ask-title">
            {`Remove scene ${sceneLabel(scene)} — ${scene.slugline ?? 'untitled'} — from the scene list?`}
          </p>
          <p className="mm">
            It leaves the scene list and every count. Nothing is deleted: it stays under Excluded with its text and can
            be restored. Locations attached to it stop covering it until then.
          </p>
          <div className="sw-ask-actions">
            <button type="button" className="ctl" data-testid="remove-scene-cancel" onClick={() => setMode('VIEW')}>
              Keep it
            </button>
            <button type="button" className="btn" data-testid="remove-scene-confirm-submit" disabled={locked} onClick={onExclude}>
              <Icon name="trash" size={15} />
              It is not a scene
            </button>
          </div>
        </div>
      ) : null}

      {excluded ? (
        <div className="sw-excluded" data-testid="scene-panel-excluded">
          <p>
            <strong>{scene.exclusionReason === 'MERGED' ? 'Merged' : 'Not a scene'}</strong>{' '}
            {scene.exclusionReason === 'MERGED'
              ? `— its text joined ${mergedInto === null ? 'another scene' : `scene ${sceneLabel(mergedInto)}`}. Split the scene to undo.`
              : '— left out of the scene list and every count. Nothing was deleted.'}
          </p>
          {scene.exclusionReason === 'NOT_A_SCENE' ? (
            <button
              type="button"
              className="btn btn-primary"
              data-testid={`scene-restore-${scene.id}`}
              disabled={locked}
              title={refusal ?? 'Put it back in the scene list'}
              onClick={onRestore}
            >
              <Icon name="undo" size={15} />
              Restore scene
            </button>
          ) : null}
        </div>
      ) : null}

      {/* ---- what looks wrong, each with the correction it names ----------- */}
      {scene.warnings.length === 0 || excluded ? null : (
        <ul className="sw-warnings" data-testid="scene-panel-warnings">
          {scene.warnings.map((w) => {
            const { title, body } = warningText(w, scene);
            // A correction already open (the heading fields, the line picker) is not offered again.
            const remedies = correctable
              ? warningRemedies(w, scene).filter((kind) => !(kind === 'EDIT' && editing) && !(kind === 'SPLIT' && mode === 'SPLIT'))
              : [];

            return (
              <li key={w} data-testid={`scene-warning-${w}`} data-confirmed={scene.status === 'CONFIRMED' ? '1' : '0'}>
                <Icon name="alert" size={16} />
                <div className="sw-warning-body">
                  <p>
                    <strong>{title}.</strong> {body}
                  </p>
                  {remedies.length === 0 ? null : (
                    <div className="sw-warning-actions">
                      {remedies.map((kind) => (
                        <button
                          key={kind}
                          type="button"
                          className="ctl sw-warning-action"
                          data-testid={`scene-warning-${w}-${kind.toLowerCase()}`}
                          disabled={correction[kind].disabled}
                          title={correction[kind].disabled ? correction[kind].why : undefined}
                          onClick={correction[kind].run}
                        >
                          <Icon name={REMEDY[kind].icon} size={14} />
                          {REMEDY[kind].label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* ---- the screenplay's words ------------------------------------- */}
      <section className="sw-detail-section" aria-label="Scene text">
        <div className="sw-section-head">
          <p className="sw-section-label">
            {typed ? 'Scene text — typed by you' : 'Script excerpt'}
            {/* The length actually held, never a fixed figure: versions parsed before
                2026-11-03 hold 2,000 characters, later ones the whole scene. */}
            {scene.textTruncated && scene.text ? ` · first ${scene.text.length.toLocaleString('en-IE')} characters` : ''}
          </p>
          {textReadOnly ? (
            <span className="sw-readonly" data-testid="scene-panel-read-only">
              <Icon name="lock" size={13} />
              Read only
            </span>
          ) : mode === 'SPLIT' ? (
            <span className="sw-readonly" data-tone="active">
              <Icon name="scissors" size={13} />
              Pick the line where the new scene starts
            </span>
          ) : null}
        </div>

        {typed && mode !== 'SPLIT' && correctable ? (
          <>
            <textarea
              className="intake-textarea scene-mono"
              data-testid="scene-panel-text-input"
              rows={8}
              value={text}
              disabled={locked}
              placeholder="What happens in this scene — action, characters, dialogue. The AI breakdown reads this text."
              onChange={(e) => {
                setText(e.target.value);
              }}
            />
            <span className="sw-hint">
              {textTooLong
                ? `Too long by ${(text.length - MAX_SCENE_TEXT_CHARS).toLocaleString('en-GB')} characters.`
                : 'The AI breakdown reads this text. The uploaded screenplay file is not changed.'}
            </span>
            {editing ? null : saveRow}
          </>
        ) : mode === 'SPLIT' ? (
          <div className="scene-text sw-excerpt" data-testid="scene-panel-split-lines">
            {lines.map(({ offset, line }, index) =>
              index === 0 || line.trim() === '' ? (
                <p key={offset} className="scene-text-line">
                  {line || ' '}
                </p>
              ) : (
                <button
                  key={offset}
                  type="button"
                  className="scene-text-line scene-split-line"
                  data-testid={`scene-split-at-${String(offset)}`}
                  data-suggested={suggested.has(offset) ? '1' : '0'}
                  data-on={splitAt === offset ? '1' : '0'}
                  aria-pressed={splitAt === offset}
                  onClick={() => {
                    setSplitAt(offset);
                    setSplitHeading(line.trim());
                  }}
                >
                  {line}
                  {suggested.has(offset) ? <span className="scene-split-hint">looks like a heading</span> : null}
                </button>
              ),
            )}
          </div>
        ) : scene.text === null ? (
          <p className="mm sw-no-text" data-testid="scene-panel-no-text">
            {typed ? 'No text was typed for this scene.' : 'No text was read under this heading.'}
          </p>
        ) : (
          <pre className="scene-text sw-excerpt" data-testid="scene-panel-text">
            {/* The line that reads like a missed heading is marked where it
                stands, so "needs attention" points at the words. */}
            {lines.map(({ offset, line }, index) => (
              <span
                key={offset}
                className={suggested.has(offset) ? 'scene-text-suggested' : undefined}
                data-testid={suggested.has(offset) ? `scene-text-suggested-${String(offset)}` : undefined}
              >
                {line}
                {index < lines.length - 1 && !suggested.has(offset) ? '\n' : ''}
              </span>
            ))}
          </pre>
        )}

        {/* The split is asked where the line is picked. */}
        {mode === 'SPLIT' ? (
          <div ref={ask} tabIndex={-1} className="sw-ask sw-split-ask" data-testid="scene-split-confirm">
            {splitAt === null ? (
              <p className="mm">Pick the line where the new scene starts. Lines that read like a heading are marked.</p>
            ) : (
              <label className="sw-field">
                <span className="sw-field-label">Heading of the new scene</span>
                <input
                  className="intake-input scene-mono"
                  data-testid="scene-split-heading"
                  value={splitHeading}
                  onChange={(e) => {
                    setSplitHeading(e.target.value);
                  }}
                />
                <span className="sw-hint">
                  This scene keeps its heading, its text up to that line, and everything attached to it. The new scene
                  takes the rest and is to be reviewed.
                </span>
              </label>
            )}
            <div className="sw-ask-actions">
              <button
                type="button"
                className="ctl"
                data-testid="scene-split-cancel"
                onClick={() => {
                  setMode('VIEW');
                  setSplitAt(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                data-testid="scene-split-submit"
                disabled={splitAt === null || splitHeading.trim() === '' || locked}
                onClick={() => {
                  if (splitAt !== null) onSplit({ at: splitAt, slugline: splitHeading.trim() });
                }}
              >
                <Icon name="scissors" size={15} />
                Split here
              </button>
            </div>
          </div>
        ) : null}
      </section>

      {/* ---- what a location for it must be like (read-only) ------------- */}
      {excluded ? null : locations}

      {error === null ? null : (
        <p className="intake-error sw-detail-error" role="alert" data-testid="scene-panel-error">
          {error}
        </p>
      )}

      {/* ---- the one thing to do ------------------------------------------
          Open to corrections: Confirm, the only red button while it is offered
          (the page's own way on steps back). Confirmed: taking it back, and the
          next scene — never red, because the page's way on is red again then. */}
      {correctable ? (
        <footer className="sw-detail-foot">
          <p className="sw-foot-note" data-testid="scene-panel-foot-note">
            {dirty ? 'Save or cancel your changes before confirming.' : 'Confirming moves on to the next scene to review.'}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="scene-panel-confirm"
            disabled={locked || dirty}
            title={dirty ? 'Save your changes first' : (refusal ?? undefined)}
            onClick={() => {
              onConfirm(true);
            }}
          >
            <Icon name="check" size={16} strokeWidth={2.6} />
            Confirm scene
          </button>
        </footer>
      ) : confirmed ? (
        <footer className="sw-detail-foot">
          <button
            type="button"
            className="sw-foot-link"
            data-testid="scene-panel-reopen"
            disabled={locked}
            title={refusal ?? 'Opens the scene for corrections again'}
            onClick={() => {
              onConfirm(false);
            }}
          >
            <Icon name="undo" size={16} />
            Mark as not reviewed
          </button>
          <button type="button" className="btn" data-testid="scene-panel-next-scene" disabled={!hasNext} onClick={onNext}>
            Next scene
            <Icon name="arrowRight" size={16} />
          </button>
        </footer>
      ) : null}
    </article>
  );
}
